<script setup lang="ts">
import { computed, h, watchEffect, type VNode } from 'vue'

import { DataTable, DataTableHead } from '@collector/shared-ui'
import { compareParticipantByShortname } from '@collector/shared-ui-domain'
import type { StatId } from '@collector/sportsapi-client-legacy'
import type {
  SportStats,
  Stat,
  SubParticipant,
} from '@collector/sportsapi-types'
import {
  createColumnHelper,
  type ColumnDef,
  type Header,
  type HeaderContext,
  type TableOptions,
} from '@tanstack/vue-table'

import type { SportsStatConfig } from '../../../interfaces/SportStatConfig'
import {
  getStatisticDecimalPlaces,
  getStatisticInputDataType,
} from '../../../interfaces/SportStatConfig/utils'
import { compareStats, isStatSame } from '../../../utils'
import StatisticInput from '../../common/Inputs/StatisticInput.vue'

type LineupId = number

type Props = {
  playerStats: SportsStatConfig[]
  initialPlayers: SubParticipant[]
}

const props = defineProps<Props>()

// state
const dirty = defineModel<boolean>('dirty', { default: false })
const players = defineModel<SubParticipant[]>('players', { required: true })

// computed
const playerToStat = computed(() => buildPlayerToStatMap(players.value))
const initialToStat = computed(() => buildPlayerToStatMap(props.initialPlayers))
const dirtyRowIds = computed(() =>
  players.value
    .filter(isPlayerDirty)
    .map((player) => player.lineup_id.toString()),
)

// table
const options: Partial<TableOptions<SubParticipant>> = {
  getRowId: (row) => row.lineup_id.toString(),
  enableSortingRemoval: false,
  sortDescFirst: true,
  initialState: { sorting: [{ id: 'shortName', desc: false }] },
}
const columnHelper = createColumnHelper<SubParticipant>()
const columns = computed(
  () =>
    [
      columnHelper.accessor((r) => r.shirt_nr, {
        id: 'shirtNr',
        header: createHeaderRenderer('#', 'w-6'),
        sortingFn: 'alphanumeric',
        meta: { sticky: 'left-0', align: 'end' },
        cell: ({ getValue, row }) =>
          h('div', { className: getDirtyClass(row.original) }, getValue()),
      }),
      columnHelper.accessor((r) => r.short_name, {
        id: 'shortName',
        header: createHeaderRenderer('Player name', 'min-w-32'),
        meta: { sticky: 'left-10', align: 'start' },
        sortingFn: (a, b) =>
          compareParticipantByShortname(a.original, b.original),
        cell: ({ getValue, row }) =>
          h(
            'div',
            { className: `text-nowrap ${getDirtyClass(row.original)}` },
            getValue(),
          ),
      }),
      ...props.playerStats.map(({ sportStat, name, format }) =>
        columnHelper.accessor((r) => getPlayerStat(sportStat.id, r), {
          id: sportStat.id.toString(),
          header: createHeaderRenderer(name ?? sportStat.name, 'min-w-16'),
          meta: { align: 'start' },
          sortingFn: (a, b) => {
            const sa = getPlayerStat(sportStat.id, a.original)
            const sb = getPlayerStat(sportStat.id, b.original)
            return compareStats(sa, sb, sportStat)
          },
          cell: ({ getValue, row }) => {
            const stat = getValue()
            return h(StatisticInput, {
              size: 'sm',
              dirty: isPlayerStatDirty(sportStat, stat, row.original),
              dataType: getStatisticInputDataType(sportStat, format),
              decimalPlaces: getStatisticDecimalPlaces(format),
              modelValue: stat,
              'onUpdate:modelValue': (stat) => updateStat(stat, row.original),
            })
          },
        }),
      ),
    ] as ColumnDef<SubParticipant, string>[],
)

function createHeaderRenderer<TData, TValue>(
  text: string,
  className?: string,
): (ctx: HeaderContext<TData, TValue>) => VNode {
  return (ctx) => {
    const header = ctx.header as Header<unknown, unknown>
    return h(DataTableHead, { className, header }, () => text)
  }
}

// methods
function isPlayerStatDirty(
  sportStat: SportStats,
  current: Stat,
  player: SubParticipant,
): boolean {
  const initialStat = getInitialPlayerStat(sportStat.id, player)
  return !isStatSame(current, initialStat, sportStat)
}

function isPlayerDirty(player: SubParticipant): boolean {
  return props.playerStats.some(({ sportStat }) => {
    const stat = getPlayerStat(sportStat.id, player)
    const initialStat = getInitialPlayerStat(sportStat.id, player)
    return !isStatSame(stat, initialStat, sportStat)
  })
}

function getDirtyClass(player: SubParticipant): string {
  return isPlayerDirty(player) ? 'font-bold' : ''
}

function updateStat(stat: Stat, player: SubParticipant): void {
  const statIndex = player.stats.findIndex((s) => s.id === stat.id)
  const updatedStats = [...player.stats]
  updatedStats.splice(statIndex, 1, stat)

  const playerIndex = players.value.findIndex((p) => p === player)
  const updatedPlayer = { ...players.value[playerIndex], stats: updatedStats }
  const updatedPlayers = [...players.value]
  updatedPlayers.splice(playerIndex, 1, updatedPlayer)

  players.value = updatedPlayers
}

function buildPlayerToStatMap(
  players: SubParticipant[],
): Map<LineupId, Map<StatId, Stat>> {
  const map = new Map<LineupId, Map<StatId, Stat>>()
  for (const player of players) {
    const statMap = new Map<StatId, Stat>()
    map.set(player.lineup_id, statMap)
    for (const stat of player.stats) statMap.set(stat.id, stat)
  }
  return map
}

function getPlayerStat(id: StatId, player: SubParticipant): Stat {
  const stats = playerToStat.value.get(player.lineup_id)
  return stats?.get(id) ?? { id, value: null }
}
function getInitialPlayerStat(id: StatId, player: SubParticipant): Stat {
  const stats = initialToStat.value.get(player.lineup_id)
  return stats?.get(id) ?? { id, value: null }
}

// watch
watchEffect(() => (dirty.value = dirtyRowIds.value.length > 0))
</script>

<template>
  <DataTable
    class="max-h-128"
    :options
    :columns
    :data="players"
    :highlightedRowIds="dirtyRowIds"
  />
</template>
